import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UniversalAddressInput from './UniversalAddressInput';
import { 
  ArrowLeft,
  MapPin,
  Save,
  Building,
  Phone,
  Clock,
  AlertCircle,
  CheckCircle,
  Radius
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BusinessLocationSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locationData, setLocationData] = useState({
    hq_address: '',
    hq_formatted_address: '',
    hq_lat: '',
    hq_lng: '',
    hq_place_id: '',
    hq_manual_entry: false,
    on_call_after_hours: false,
    can_receive_overflow: true,
    service_radius_km: 80
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/profile`);
      const userData = response.data;
      setProfile(userData);
      
      // Populate location data
      setLocationData({
        hq_address: userData.hq_address || '',
        hq_formatted_address: userData.hq_formatted_address || '',
        hq_lat: userData.hq_lat?.toString() || '',
        hq_lng: userData.hq_lng?.toString() || '',
        hq_place_id: userData.hq_place_id || '',
        hq_manual_entry: userData.hq_manual_entry || false,
        on_call_after_hours: userData.on_call_after_hours || false,
        can_receive_overflow: userData.can_receive_overflow !== false,
        service_radius_km: userData.service_radius_km || 80
      });
      
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load business information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        hq_address: locationData.hq_address || null,
        hq_lat: locationData.hq_lat ? parseFloat(locationData.hq_lat) : null,
        hq_lng: locationData.hq_lng ? parseFloat(locationData.hq_lng) : null,
        on_call_after_hours: locationData.on_call_after_hours,
        can_receive_overflow: locationData.can_receive_overflow,
        service_radius_km: parseInt(locationData.service_radius_km) || 80
      };
      
      await axios.put(`${API}/profile/location`, updateData);
      
      toast.success('Business location updated successfully');
      fetchProfile();
      
    } catch (error) {
      console.error('Failed to update location:', error);
      toast.error('Failed to update business location');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setLocationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateCoordinates = (lat, lng) => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    return !isNaN(latNum) && !isNaN(lngNum) && 
           latNum >= -90 && latNum <= 90 && 
           lngNum >= -180 && lngNum <= 180;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <Building className="h-6 w-6 mr-2" />
                  Business Location
                </h1>
                <p className="text-sm text-gray-600">Configure your business location for emergency call routing</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Business Information */}
        <div className="jop-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded">{profile?.business_name}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <div className="text-sm text-gray-900 p-2 bg-gray-50 rounded">{profile?.phone}</div>
            </div>
          </div>
        </div>

        {/* Location Configuration */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <MapPin className="h-5 w-5 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Headquarters Location</h2>
          </div>

          <div className="space-y-6">
            <UniversalAddressInput
              value={locationData.hq_address}
              onChange={(value) => handleInputChange('hq_address', value)}
              onAddressSelect={(addressData) => {
                setLocationData(prev => ({
                  ...prev,
                  hq_formatted_address: addressData.formatted_address,
                  hq_lat: addressData.lat?.toString() || '',
                  hq_lng: addressData.lng?.toString() || '',
                  hq_place_id: addressData.place_id || '',
                  hq_manual_entry: addressData.manual_entry
                }));
              }}
              placeholder="123 Main Street, Newcastle NSW 2300"
              label="Business Address"
              showCoordinates={true}
            />

            {locationData.hq_manual_entry && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 text-amber-600 mr-2" />
                  <span className="text-sm text-amber-700">
                    This address was entered manually and may require admin validation for accurate emergency routing.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Configuration */}
        <div className="jop-card p-6 mb-6">
          <div className="flex items-center mb-6">
            <Radius className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Service Configuration</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Radius (km)
              </label>
              <input
                type="number"
                min="5"
                max="200"
                value={locationData.service_radius_km}
                onChange={(e) => handleInputChange('service_radius_km', parseInt(e.target.value) || 80)}
                className="jop-input"
                placeholder="80"
              />
              <div className="mt-1 text-xs text-gray-500">
                Maximum distance you're willing to travel for emergency jobs
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={locationData.can_receive_overflow}
                  onChange={(e) => handleInputChange('can_receive_overflow', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Available for emergency overflow jobs
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={locationData.on_call_after_hours}
                  onChange={(e) => handleInputChange('on_call_after_hours', e.target.checked)}
                  className="rounded border-gray-300 text-red-600 shadow-sm focus:border-red-500 focus:ring-red-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Available for after-hours emergency calls
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="jop-button flex items-center"
          >
            {saving ? (
              <div className="spinner" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Business Location
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessLocationSettings;